import { useEffect, useMemo, useRef, useState } from 'react';

import { toast } from 'sonner';
import { z } from 'zod';

import { ButtonGroup } from './shadcn/button-group';
import { PendingInviteBulkAddResponseDto } from '../../../../backend/src/auth/pending-matter-invite/dto/pending-invite-bulk-add.response.dto';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

const emailSchema = z.email();

interface Recipient {
  email: string;
  valid: boolean;
}

type RecipientStatus = 'already-in-matter' | 'already-in-org' | 'needs-email-invitation';

interface StagedRecipient extends Recipient {
  status: RecipientStatus;
}

export const parseInviteRecipients = (value: string): Recipient[] => {
  const seen = new Set<string>();
  return value
    .split(',')
    .map((email) => email.trim().toLowerCase())
    .filter((email) => email.length > 0 && !seen.has(email) && seen.add(email))
    .map((email) => ({ email, valid: emailSchema.safeParse(email).success }));
};

interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultEmail?: string;
  existingMatterUserEmails?: string[];
  organisationUserEmails?: string[];
  onInvite: (emails: string[]) => Promise<PendingInviteBulkAddResponseDto>;
}

export function InviteUserDialog({
  open,
  onOpenChange,
  defaultEmail = '',
  existingMatterUserEmails = [],
  organisationUserEmails = [],
  onInvite,
}: InviteUserDialogProps) {
  const [input, setInput] = useState('');
  const [recipients, setRecipients] = useState<StagedRecipient[]>([]);
  const [isSending, setIsSending] = useState(false);
  const recipientsScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setInput(defaultEmail.trim());
      setRecipients([]);
    }
  }, [open, defaultEmail]);

  const pendingRecipients = useMemo(() => parseInviteRecipients(input), [input]);
  const existingMatterEmails = useMemo(
    () => new Set(existingMatterUserEmails.map((email) => email.toLowerCase())),
    [existingMatterUserEmails],
  );
  const organisationEmails = useMemo(
    () => new Set(organisationUserEmails.map((email) => email.toLowerCase())),
    [organisationUserEmails],
  );
  const invitableRecipients = recipients.filter(({ status }) => status !== 'already-in-matter');

  const stageInput = () => {
    if (pendingRecipients.length === 0) return;
    const invalidCount = pendingRecipients.filter(({ valid }) => !valid).length;
    const validPendingRecipients = pendingRecipients
      .filter(({ valid }) => valid)
      .map((recipient) => ({
        ...recipient,
        status: existingMatterEmails.has(recipient.email)
          ? ('already-in-matter' as const)
          : organisationEmails.has(recipient.email)
            ? ('already-in-org' as const)
            : ('needs-email-invitation' as const),
      }));
    const currentEmails = new Set(recipients.map(({ email }) => email));
    const stagedRecipients = [
      ...recipients,
      ...validPendingRecipients.filter(({ email }) => !currentEmails.has(email)),
    ];

    if (invalidCount > 0) {
      toast.error(
        `${invalidCount} invalid ${invalidCount === 1 ? 'email address was' : 'email addresses were'} not added.`,
      );
    }
    setRecipients(stagedRecipients);
    setInput('');
    requestAnimationFrame(() => {
      recipientsScrollRef.current?.scrollTo({ top: recipientsScrollRef.current.scrollHeight, behavior: 'smooth' });
    });
  };

  const removeRecipient = (email: string) => {
    setRecipients((current) => current.filter((recipient) => recipient.email !== email));
  };

  const handleSend = async () => {
    if (invitableRecipients.length === 0) return;
    if (invitableRecipients.length > 1000) {
      toast.error('You can invite up to 1,000 users at a time.');
      return;
    }

    setIsSending(true);
    try {
      const response = await onInvite(invitableRecipients.map(({ email }) => email));
      const failedEmails = new Set(
        response.results.filter(({ status }) => status === 'failed').map(({ userEmail }) => userEmail),
      );
      if (failedEmails.size > 0) {
        setRecipients(recipients.filter(({ email }) => failedEmails.has(email)));
        setInput('');
      } else {
        onOpenChange(false);
      }
    } catch {
      toast.error('Invitations could not be sent. Please try again.');
      setInput('');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !isSending && onOpenChange(nextOpen)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--user-plus" /> Invite users to Wexler
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="px-3">
            If a user is already in your organisation, they'll be added to this Matter straight away.
          </p>
          <p className="px-3">
            If a user isn't on Wexler yet, they'll be sent an email invitation and be added to this Matter when their
            account is provisioned.{' '}
          </p>

          <div className="mt-5 rounded-lg bg-gray-100">
            <ButtonGroup className="w-full rounded-md ring-offset-2 has-[>input:focus-visible]:ring-1 has-[>input:focus-visible]:ring-teal-500">
              <Input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    stageInput();
                  }
                }}
                placeholder="name@example.com, colleague@example.com"
                autoFocus
                disabled={isSending}
                className="border-gray-300 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <Button type="button" onClick={stageInput} disabled={isSending} className="border-gray-300">
                Add Users
              </Button>
            </ButtonGroup>
            <p className="flex items-center gap-2 px-3.5 py-2.5 text-xs text-gray-500">
              <Icon className="lucide--info text-gray-400" /> Enter addresses separated by commas to add users in bulk.
            </p>
          </div>

          <div className="mt-6">
            <div className="px-3 pb-2 text-sm font-medium text-gray-600">
              Recipients <span className="font-normal text-gray-400">({recipients.length})</span>{' '}
            </div>

            <hr />

            <div
              ref={recipientsScrollRef}
              className="recipients-scroll-fade max-h-64 min-h-28 divide-y divide-gray-200 overflow-y-auto"
            >
              {recipients.length === 0 ? (
                <div className="flex h-28 items-center justify-center px-3 py-1 text-center text-gray-400">
                  <p className="max-w-sm text-balance">
                    Add email addresses above to review them before sending invitations.
                  </p>
                </div>
              ) : (
                recipients.map(({ email, status }) => (
                  <div key={email} className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <div className="min-w-0">
                      <span className={status === 'already-in-matter' ? 'text-gray-400' : 'mr-2 text-gray-700'}>
                        {email}
                      </span>
                      {status === 'already-in-matter' && (
                        <span className="text-gray-400"> (already in this Matter)</span>
                      )}
                      {status === 'needs-email-invitation' && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500"
                              tabIndex={0}
                            >
                              <Icon className="lucide--send" />
                              Email Invitation
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[18rem] text-center">
                            This user doesn't have an account yet. They'll receive an email invitation and be added to
                            this Matter automatically on sign in.
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {status === 'already-in-org' && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500"
                              tabIndex={0}
                            >
                              <Icon className="lucide--building-2" />
                              In your organisation
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[18rem] text-center">
                            This user is already in your organisation and will be added to this Matter
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>

                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeRecipient(email)}
                      disabled={isSending}
                      aria-label={`Remove ${email}`}
                    >
                      <Icon className="lucide--x" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={isSending}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSend}
            disabled={isSending || invitableRecipients.length === 0}
          >
            Send invitations or add to Matter ({invitableRecipients.length})
            <Icon className={isSending ? 'lucide--loader animate-spin ease-in-out' : 'lucide--chevron-right'} />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
